import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { EntityTypeService } from '@core/services/entity-type/entity-type.service';
import { SecurityTypeService } from '@core/services/security-type/security-type.service';
import { DrawerSetting, GridColumn, PrimeGridOption } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { ODataFilter } from '@shared/modules/grid/abstractions/odata-filter-column';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { asyncFilterOptionsFn } from '@shared/modules/prime-grid-filters/helpers/functions/async-filter-option';
import { DialogFactoryService } from '@shared/services/dialog-factory.service';
import { QueryOptions } from 'odata-query';
import { map } from 'rxjs';
import { GstarClient } from 'src/app/web-api-client';
import {
  Collection,
  EntityType,
  GeneralRatesRuleEngine,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  InvestmentJurisdiction,
  SecurityType,
} from '../../models';
import { InvestmentJurisdictionService } from '../../services';
import { AdminPrimeGridComponent } from '../admin-prime-grid/admin-prime-grid.component';
import { AdminExplanationEditorComponent } from '../explanation-editor/explanation-editor.component';
import { UpsertGeneralRatesRuleEngineComponent } from '../upsert-general-rates-rule-engine/upsert-general-rates-rule-engine.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-general-rates-rule-engine-grid',
  templateUrl: 'general-rates-rule-engine-grid.component.html',
  standalone: false,
})
export class GeneralRatesRuleEngineGridComponent implements OnInit {
  private translateService = inject(TranslateService);
  private dialogFactoryService = inject(DialogFactoryService);
  private entityTypeService = inject(EntityTypeService);
  private securityTypeService = inject(SecurityTypeService);
  private investmentJurisdictionService = inject(InvestmentJurisdictionService);
  private gstarClient = inject(GstarClient);

  private readonly allowedSecurityTypeNames = new Set<string>([
    'Corporate Debt',
    'Government Debt',
    'Listed Equity',
    'Unlisted Equity',
  ]);

  source: InputSignal<Collection<GeneralRatesRuleEngine>> = input.required({});

  query = input<Partial<QueryOptions<GeneralRatesRuleEngine>>>();
  queryWritable = signal<Partial<QueryOptions<GeneralRatesRuleEngine>>>({});

  queryChange = output<Partial<QueryOptions<GeneralRatesRuleEngine>>>();
  create = output<GridItemCreateEvent<GeneralRatesRuleEngine>>();
  update = output<GridItemUpdateEvent<GeneralRatesRuleEngine>>();
  selectRows = output<GeneralRatesRuleEngine[]>();
  deleteRequested = output<GeneralRatesRuleEngine>();

  internalSelectedItems = signal<GeneralRatesRuleEngine[]>([]);

  actionMenuTitle = this.translateService.instant(
    'admin.grid.actionsColumnName'
  );

  searchSetting: SearchSetting<GeneralRatesRuleEngine> = {
    filtersLogicalOperator: 'or',
    filters: [
      { dataKey: 'entityTypeName', operator: 'contains' },
      { dataKey: 'investmentJurisdictionNames', operator: 'contains' },
      { dataKey: 'securityTypeNames', operator: 'contains' },
      { dataKey: 'incomeTypeName', operator: 'contains' },
      { dataKey: 'rateDisplay', operator: 'contains' },
      { dataKey: 'explanation', operator: 'contains' },
    ],
  };

  setting: PrimeGridOption<GeneralRatesRuleEngine> = {
    lazy: true,
    hasCheckbox: true,
    disableSelectAllModal: true,
  };

  drawerSetting: DrawerSetting<GeneralRatesRuleEngine> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createGeneralRatesRuleEngineTitle'
      ),
      component: UpsertGeneralRatesRuleEngineComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateGeneralRatesRuleEngineTitle'
      ),
      component: UpsertGeneralRatesRuleEngineComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update.emit({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  grid =
    viewChild<
      AdminPrimeGridComponent<GeneralRatesRuleEngine, GeneralRatesRuleEngine>
    >('grid');

  actionMenu: ActionButton<GeneralRatesRuleEngine>[] = this.buildActionMenu();

  private buildActionMenu(): ActionButton<GeneralRatesRuleEngine>[] {
    const isBulkEdit = this.internalSelectedItems().length > 1;

    const actionButtons: ActionButton<GeneralRatesRuleEngine>[] = [
      {
        label: isBulkEdit
          ? this.translateService.instant('portfolio.actionMenu.editAll')
          : this.translateService.instant('portfolio.actionMenu.edit'),
        icon: '',
        command: (item: GeneralRatesRuleEngine): void => {
          const selected = this.internalSelectedItems();
          const isMultiEdit = selected.length > 1;

          this.grid()?.editActionClick(item, {
            isBulkEdit: isMultiEdit,
            bulkItems: isMultiEdit ? selected : [],
            drawerTitleOverride: this.translateService.instant(
              'admin.grid.updateGeneralRatesRuleEngineTitle'
            ),
            primaryLabelOverride: isMultiEdit
              ? this.translateService.instant('admin.grid.applyToAll')
              : this.translateService.instant('admin.grid.update'),
          });
        },
      },
    ];

    if (!isBulkEdit) {
      actionButtons.push({
        label: this.translateService.instant('shared.delete'),
        icon: 'icon-delete-outline',
        command: (item: GeneralRatesRuleEngine): void => {
          this.deleteRequested.emit(item);
        },
      });
    }

    return actionButtons;
  }

  /**
   * Open the explanation rich text editor.
   * @param item The general rates rule engine item to view the explanation for.
   * @returns void
   */
  openExplanationViewer(item: GeneralRatesRuleEngine): void {
    const content = item.explanationHtml || item.explanation || '';

    if (!content.trim()) {
      return;
    }

    this.dialogFactoryService.open(
      this.translateService.instant('admin.generalRatesRuleEngine.explanation'),
      AdminExplanationEditorComponent,
      {
        cancelLabel: this.translateService.instant('shared.close'),
        hideConfirm: true,
        data: {
          title: this.translateService.instant(
            'admin.generalRatesRuleEngine.explanation'
          ),
          content,
          readonly: true,
        },
      }
    );
  }

  headers: GridColumn[] = [
    {
      label: this.translateService.instant(
        'admin.generalRatesRuleEngine.entityType'
      ),
      key: 'entityTypeName',
      sortKey: 'entityType.name',
      dataType: DataType.String,
      isSortable: true,
      isFilterable: true,
      filterableColumn: {
        type: FilterTypes.Combobox,
        oDataPath: 'entityTypeId',
        options: {
          isMultiple: true,
          allowSearch: true,
          items: asyncFilterOptionsFn(
            this.entityTypeService.get({
              filter: 'isActive eq true and isDefault eq false',
            }),
            ({ items }: Collection<EntityType>) => {
              return items.map((entityType: EntityType) => {
                return {
                  value: entityType.entityTypeId
                    ? entityType.entityTypeId.toString()
                    : '',
                  key: entityType.name ? entityType.name.toString() : '',
                } as KeyValue<string, string>;
              });
            }
          ),
        },
        oDataQueryBuilder: (columnFilter: ODataFilter): void => {
          columnFilter.updateQuery({
            in: columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map((item: KeyValue<string, string>) => Number(item.value))
              .filter((item: number) => !isNaN(item)),
          });
        },
      },
    },
    {
      label: this.translateService.instant(
        'admin.generalRatesRuleEngine.investmentJurisdictions'
      ),
      key: 'investmentJurisdictionNames',
      sortKey: 'investmentJurisdiction.country.name',
      dataType: DataType.String,
      isSortable: true,
      isFilterable: true,

      filterableColumn: {
        type: FilterTypes.Combobox,
        oDataPath: 'investmentJurisdictionId',
        options: {
          isMultiple: true,
          allowSearch: true,
          items: asyncFilterOptionsFn(
            this.investmentJurisdictionService.getAllInvestmentJurisdiction(),
            ({ items }: Collection<InvestmentJurisdiction>) => {
              return items.map(
                (investmentJurisdiction: InvestmentJurisdiction) => {
                  return {
                    value: investmentJurisdiction.investmentJurisdictionId
                      ? investmentJurisdiction.investmentJurisdictionId.toString()
                      : '',
                    key: investmentJurisdiction.name
                      ? investmentJurisdiction.name.toString()
                      : '',
                  } as KeyValue<string, string>;
                }
              );
            }
          ),
        },
        oDataQueryBuilder: (columnFilter: ODataFilter): void => {
          columnFilter.updateQuery({
            in: columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map((item: KeyValue<string, string>) => Number(item.value))
              .filter((item: number) => !isNaN(item)),
          });
        },
      },
    },
    {
      label: this.translateService.instant(
        'admin.generalRatesRuleEngine.securityTypes'
      ),
      key: 'securityTypeNames',
      sortKey: 'securityType.name',
      dataType: DataType.String,
      isSortable: true,
      isFilterable: true,
      filterableColumn: {
        type: FilterTypes.Combobox,
        oDataPath: 'securityType/name',
        options: {
          isMultiple: true,
          allowSearch: true,
          items: asyncFilterOptionsFn(
            this.securityTypeService.activeSecurityTypeList(),
            ({ items }: Collection<SecurityType>) => {
              return items
                .filter((securityType: SecurityType) =>
                  this.allowedSecurityTypeNames.has(securityType.name)
                )
                .map((securityType: SecurityType) => {
                  return {
                    value: securityType.securityTypeId
                      ? securityType.securityTypeId.toString()
                      : '',
                    key: securityType.name ? securityType.name.toString() : '',
                  } as KeyValue<string, string>;
                });
            }
          ),
        },
        oDataQueryBuilder: (columnFilter: ODataFilter): void => {
          columnFilter.updateQuery({
            in: columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map((item: KeyValue<string, string>) => item.key),
          });
        },
      },
    },
    {
      label: this.translateService.instant(
        'admin.generalRatesRuleEngine.incomeType'
      ),
      key: 'incomeTypeName',
      sortKey: 'incomeTypeName',
      dataType: DataType.String,
      isSortable: true,
      isFilterable: true,
      filterableColumn: {
        type: FilterTypes.Combobox,
        oDataPath: 'incomeTypeName',
        options: {
          isMultiple: true,
          allowSearch: true,
          items: this.gstarClient
            .taxresultitemtypesGET(
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined
            )
            .pipe(
              map(response => {
                const optionsMap = new Map<string, KeyValue<string, string>>();

                (response.value ?? []).forEach(securityTypeGroup => {
                  (securityTypeGroup.taxResultItemTypes ?? []).forEach(
                    incomeType => {
                      if (!incomeType.name || !incomeType.taxResultItemTypeId) {
                        return;
                      }

                      optionsMap.set(incomeType.name, {
                        value: incomeType.taxResultItemTypeId.toString(),
                        key: incomeType.name,
                      });
                    }
                  );
                });

                return Array.from(optionsMap.values());
              })
            ),
        },
        oDataQueryBuilder: (columnFilter: ODataFilter): void => {
          columnFilter.updateQuery({
            in: columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map((item: KeyValue<string, string>) => item.key),
          });
        },
      },
    },
    {
      label: this.translateService.instant('admin.generalRatesRuleEngine.rate'),
      key: 'rateDisplay',
      sortKey: 'rate',
      dataType: DataType.String,
      isSortable: true,
      isFilterable: true,
      filterableColumn: {
        type: FilterTypes.TextField,
        options: {
          multiEntryByEnter: true,
        },
        oDataQueryBuilder: (columnFilter): void => {
          const value = columnFilter.getValue<string | string[]>();

          if (Array.isArray(value)) {
            columnFilter.updateQuery({
              in: value,
            });
            return;
          }

          columnFilter.updateQuery({
            contains: value,
          });
        },
      },
    },
    {
      label: this.translateService.instant(
        'admin.generalRatesRuleEngine.explanation'
      ),
      key: 'explanation',
      sortKey: 'description',
      dataType: DataType.String,
      isSortable: true,
      width: 320,
      isFilterable: true,
      onClick: (rowData: unknown): void => {
        this.openExplanationViewer(rowData as GeneralRatesRuleEngine);
      },
      filterableColumn: {
        type: FilterTypes.TextField,
        oDataPath: 'description',
        oDataQueryBuilder: (columnFilter): void => {
          columnFilter.updateQuery({
            contains: columnFilter.getValue<string>(),
          });
        },
      },
    },
  ];

  ngOnInit(): void {
    this.queryWritable.set(this.query() ?? {});
  }

  queryHandler(query: Partial<QueryOptions<GeneralRatesRuleEngine>>): void {
    this.queryWritable.update(
      (previousQuery: Partial<QueryOptions<GeneralRatesRuleEngine>>) => {
        return {
          ...previousQuery,
          ...query,
        };
      }
    );
    this.queryChange.emit(this.queryWritable());
  }

  onItemsSelected(selection: GeneralRatesRuleEngine[]): void {
    this.internalSelectedItems.set(selection);
    this.actionMenu = this.buildActionMenu();
    this.selectRows.emit(selection);
  }

  clearSelection(): void {
    this.internalSelectedItems.set([]);
    this.actionMenu = this.buildActionMenu();
    this.grid()?.clearSelection();
    this.selectRows.emit([]);
  }
}
